import {
  createFileRoute,
  Link,
  Outlet,
  useRouterState,
  type MakeRouteMatchUnion,
} from "@tanstack/react-router";
import {
  useTranslation,
  TRANSLATION_NAMESPACE,
} from "@bfc-software/bfc-web-ui/i18n";
import { PageHeader } from "@bfc-software/pallet";
import {
  AuthContext,
  AuthUtils,
  AUTHENTICATED,
  CREATING_SESSION,
} from "@bfc-software/app-toolbox";
import { AppHeader } from "@bfc-software/bfc-web-ui/ui/shared/app-header";
import { PrimarySidebar } from "./-components/primary-sidebar";
import { LocationSelector } from "../../../-components/location-selector";
import { WarehouseGate } from "../../../-components/warehouse-gate";
import { useContext, useEffect } from "react";
import {
  useCommandPalette,
  navigationCommand,
} from "@bfc-software/bfc-web-ui/ui/shared/command-palette";
// eslint-disable-next-line no-restricted-imports -- follow-up: use useCurrentLocalization()
import { TFunction } from "i18next";

import { getEnv } from "@bfc-software/bfc-web-ui/environment";

import styles from "./_layout.module.css";

export const Route = createFileRoute(
  "/$lang/_layout/orgs/$org/_layout/internal/whs/$location/_layout",
)({
  component: RouteComponent,
  staticData: {
    crumb: (match: MakeRouteMatchUnion) => {
      const params = match.params as { location?: string };
      return { label: params.location ?? "" };
    },
  },
});

export type Crumb = {
  label: string;
  to?: string;
  params?: Record<string, string>;
};

type CrumbEnabledRouteStaticData = {
  crumb: (match: MakeRouteMatchUnion, t: TFunction) => Crumb | Crumb[];
};

function getCrumbsFromMatch(
  routeDef: MakeRouteMatchUnion,
  t: TFunction,
): Array<[string, Crumb]> {
  const staticData = (routeDef.staticData ?? {}) as CrumbEnabledRouteStaticData;
  if (typeof staticData.crumb !== "function") {
    return [];
  }
  const result = staticData.crumb(routeDef, t);
  const crumbs = Array.isArray(result) ? result : [result];
  return crumbs.map((crumb, i) => [`${routeDef.id}:${i}`, crumb]);
}

export function useCrumbs(): Array<[string, Crumb]> {
  const params = Route.useParams();
  const { t } = useTranslation({
    lang: params.lang,
    namespace: TRANSLATION_NAMESPACE.warehouse,
  });

  return useRouterState({
    select: (s) => s.matches.flatMap((m) => getCrumbsFromMatch(m, t)),
  });
}

function renderCrumb([id, crumb]: [string, Crumb], isLast: boolean) {
  return (
    <div key={id} className={styles.crumb}>
      {crumb.to && !isLast ? (
        <Link to={crumb.to} params={crumb.params} className={styles.crumbLink}>
          {crumb.label}
        </Link>
      ) : (
        <span className={styles.crumbLabel}>{crumb.label}</span>
      )}
    </div>
  );
}

function RouteComponent() {
  const params = Route.useParams();
  const crumbs = useCrumbs();
  const { state } = useContext(AuthContext);
  const { register } = useCommandPalette();

  const orgKnown =
    state.status === AUTHENTICATED || state.status === CREATING_SESSION;
  // eslint-disable-next-line no-restricted-properties -- necessary: PageHeader shows the org name before/outside the gate, so there is no tenancy context yet, and AuthUtils has no org-name-by-slug lookup (only getOrgIdByStub + getOrgName)
  const orgId = orgKnown ? AuthUtils.getOrgIdByStub(state, params.org) : null;

  useEffect(() => {
    // Every command below points into _internal. Registering them for someone
    // who cannot open them would surface the tooling in search results and then
    // 404 on selection.
    if (!getEnv().internalToolsEnabled) return;

    register([
      navigationCommand({
        id: "org-location-home",
        title: "global.commandPalette.commands.orgHome",
        to: { to: "/$lang/orgs/$org/internal/whs/$location/home" },
        keywords: ["home"],
      }),
      navigationCommand({
        id: "org-location-ftp",
        title: "global.commandPalette.commands.orgFTP",
        to: { to: "/$lang/orgs/$org/internal/whs/$location/ftp" },
        keywords: ["ftp", "history"],
      }),

      navigationCommand({
        id: "org-location-task-plans",
        title: "global.commandPalette.commands.orgTaskPlans",
        to: { to: "/$lang/orgs/$org/internal/whs/$location/task-plans" },
        keywords: ["tasks", "plans", "task-plans"],
      }),

      navigationCommand({
        id: "org-location-selector-sessions",
        title: "global.commandPalette.commands.orgSelectorSessions",
        to: { to: "/$lang/orgs/$org/internal/whs/$location/selector-sessions" },
        keywords: ["selectors", "sessions", "shift"],
      }),

      navigationCommand({
        id: "org-location-webhooks",
        title: "global.commandPalette.commands.orgWebhooks",
        to: {
          to: "/$lang/orgs/$org/internal/whs/$location/webhooks/endpoints",
        },
        keywords: ["webhooks", "endpoints"],
      }),

      navigationCommand({
        id: "org-location-ftp-config",
        title: "global.commandPalette.commands.orgFTPConfig",
        to: { to: "/$lang/orgs/$org/internal/whs/$location/ftp-config" },
        keywords: ["ftp", "config", "configuration"],
      }),
    ]);
  }, [params.lang, params.location, params.org, register]);

  const { t } = useTranslation({
    lang: params.lang,
    namespace: TRANSLATION_NAMESPACE.warehouse,
  });

  const orgName = orgId ? AuthUtils.getOrgName(state, orgId) : null;

  return (
    <>
      <AppHeader
        nav={<PrimarySidebar />}
        actions={<LocationSelector className={styles.warehouseMenu} />}
      />
      <PageHeader title={orgName ?? t("header")}>
        <div className={styles.crumbs}>
          {crumbs.map((crumb, i) =>
            renderCrumb(crumb, i === crumbs.length - 1),
          )}
        </div>
      </PageHeader>
      <WarehouseGate
        lang={params.lang}
        org={params.org}
        location={params.location}
        fallbackTo="/$lang/orgs/$org/internal"
        loading={<p className={styles.switching}>{t("loadingWarehouse")}</p>}
      >
        <Outlet />
      </WarehouseGate>
    </>
  );
}
